import { useEffect, useMemo, useState, useRef } from 'react';
import { bool, func, oneOf, shape, number } from 'prop-types';
import { isEmpty, isFinite } from 'lodash';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import { Trans, t } from '@lingui/macro';
import { TextField } from '@material-ui/core';

import useStyles from './ReportDialogStyles';
import { usePageSize } from '../page/PageUtils';

export const SIZES = {
    AUTO: 'auto',
    SMALL: 'small',
    MEDIUM: 'medium',
    LARGE: 'large',
};

const VIEWPORTS = {
    [SIZES.SMALL]: { width: 1024, height: 768 },
    [SIZES.MEDIUM]: { width: 1440, height: 1024 },
    [SIZES.LARGE]: { width: 1920, height: 1200 },
};

export const pdfOptionsType = shape({
    size: oneOf(Object.values(SIZES)),
    maxTableRows: number,
});

export const defaultPdfOptions = {
    size: SIZES.AUTO,
};

export default function PdfOptions({ options, requireTableRows, minTableRows, maxTableRows, onChange, onValidate }) {
    const classes = useStyles();
    const pageSize = usePageSize();
    const [size, setSize] = useState(options.size);
    const [tableRows, setTableRows] = useState(options.maxTableRows || (requireTableRows ? 100 : undefined));
    const [tableRowsError, setTableRowsError] = useState(undefined);
    const tableRowsValid = useMemo(() => {
        const numericValue = Number(tableRows);
        const isNumeric = isFinite(numericValue);
        const inRange = isNumeric && numericValue >= minTableRows && numericValue <= maxTableRows;

        if (!isNumeric && requireTableRows) {
            setTableRowsError(t`A table rows value is required`);
            return false;
        }

        if (isNumeric && !inRange) {
            setTableRowsError(t`Please enter a value between ${minTableRows} and ${maxTableRows}`);
            return false;
        }

        setTableRowsError(undefined);
        return true;
    }, [tableRows, minTableRows, maxTableRows, requireTableRows]);

    const onChangeCallback = useRef();
    const onValidateCallback = useRef();
    const getViewport = useRef();

    onChangeCallback.current = onChange;
    onValidateCallback.current = onValidate;
    getViewport.current = (selectedSize) => {
        if (selectedSize === SIZES.AUTO) return pageSize;
        return VIEWPORTS[selectedSize];
    };

    useEffect(() => {
        onChangeCallback.current({
            size,
            viewport: getViewport.current(size),
            maxTableRows: Number(tableRows),
        });
    }, [size, tableRows]);

    useEffect(() => {
        onValidateCallback.current(tableRowsValid);
    }, [tableRowsValid]);

    return (
        <div className={classes.pdfOptions}>
            <FormControl>
                <InputLabel id="report-size-label">
                    <Trans>Page Size</Trans>
                </InputLabel>
                <Select
                    labelId="report-size-label"
                    id="report-size"
                    value={size}
                    onChange={(event) => setSize(event.target.value)}
                >
                    <MenuItem value={SIZES.AUTO}>
                        <Trans>Auto</Trans>
                    </MenuItem>
                    <MenuItem value={SIZES.SMALL}>
                        <Trans>Small</Trans>
                    </MenuItem>
                    <MenuItem value={SIZES.MEDIUM}>
                        <Trans>Medium</Trans>
                    </MenuItem>
                    <MenuItem value={SIZES.LARGE}>
                        <Trans>Large</Trans>
                    </MenuItem>
                </Select>
            </FormControl>
            <FormControl>
                <TextField
                    id="table-rows"
                    type="number"
                    label={t`Table Rows`}
                    value={tableRows || ''}
                    onChange={(e) => setTableRows(!isEmpty(e.target.value) ? e.target.value : undefined)}
                    placeholder={requireTableRows ? undefined : t`Auto`}
                    required={requireTableRows}
                    error={!!tableRowsError}
                    helperText={tableRowsError || t`Number of rows to display in tables`}
                    InputLabelProps={{
                        shrink: true,
                    }}
                />
            </FormControl>
        </div>
    );
}

PdfOptions.propTypes = {
    options: pdfOptionsType.isRequired,
    requireTableRows: bool,
    minTableRows: number,
    maxTableRows: number,
    onChange: func.isRequired,
    onValidate: func.isRequired,
};

PdfOptions.defaultProps = {
    requireTableRows: false,
    minTableRows: 1,
    maxTableRows: 250,
};
